import { useState } from 'react';
import { Alert, Button } from '@/components/ui';
import { api } from '@/lib/api';
import { errorMessage } from '@/lib/errors';
import { formatBRL, formatDateTime, parseMoneyInput } from '@/lib/format';
import { nomeMetodo, type ParcelaVenda } from '@/lib/pagamentos';
import { useApi } from '@/lib/useApi';

interface Externo {
  id: string;
  data: string | null;
  valor: string;
  liquido: string | null;
  parcelas: number;
  meio: string | null;
  descricao: string;
  pagador: string | null;
}

interface VendaOpcao {
  id: string;
  numero: number | null;
  clienteNome: string;
  valor: string;
  dataVenda: string;
}

/** Gestão: pagamentos que caíram no Mercado Pago sem passar por um link do sistema. */
export function PagamentosExternos() {
  const { data, error, reload } = useApi<Externo[]>('/crm/mp-payments');
  return (
    <section className="card">
      <h2>Pagamentos do Mercado Pago fora do sistema</h2>
      <p className="muted small">
        Pagamentos aprovados nos últimos 15 dias que não vieram de um link gerado aqui (por exemplo, links criados direto no Mercado Pago). Ligue cada um à venda
        para o sistema dar baixa no Conta Azul.
      </p>
      {error && <Alert>{error}</Alert>}
      {!data ? (
        !error && <p className="muted">Consultando o Mercado Pago…</p>
      ) : data.length === 0 ? (
        <p className="muted small">Nenhum pagamento pendente de ligação.</p>
      ) : (
        <div className="links-list">
          {data.map((p) => (
            <ItemExterno key={p.id} p={p} onDone={() => void reload()} />
          ))}
        </div>
      )}
    </section>
  );
}

function ItemExterno({ p, onDone }: { p: Externo; onDone: () => void }) {
  const [aberto, setAberto] = useState(false);
  const [vendas, setVendas] = useState<VendaOpcao[] | null>(null);
  const [vendaId, setVendaId] = useState('');
  const [parcelas, setParcelas] = useState<ParcelaVenda[] | null>(null);
  const [parcelaId, setParcelaId] = useState('');
  const [valor, setValor] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function abrir() {
    setAberto(true);
    setErro(null);
    try {
      setVendas(await api.get<VendaOpcao[]>(`/crm/mp-payments/vendas?valor=${p.liquido ?? p.valor}`));
    } catch (e) {
      setErro(errorMessage(e));
    }
  }

  async function escolherVenda(id: string) {
    setVendaId(id);
    setParcelas(null);
    setParcelaId('');
    if (!id) return;
    try {
      const ps = await api.get<ParcelaVenda[]>(`/crm/sales/${id}/parcelas`);
      setParcelas(ps);
      const abertas = ps.filter((x) => x.aberta);
      if (abertas.length === 1 && abertas[0]) escolherParcela(abertas[0]);
    } catch (e) {
      setErro(errorMessage(e));
    }
  }

  function escolherParcela(x: ParcelaVenda) {
    setParcelaId(x.id);
    const sugerido = Math.min(x.naoPago, Number(p.valor));
    setValor(sugerido.toFixed(2).replace('.', ','));
  }

  async function confirmar() {
    const v = parseMoneyInput(valor);
    if (!vendaId || !parcelaId) return setErro('Escolha a venda e a parcela.');
    if (!v || Number(v) <= 0) return setErro('Digite quanto desta parcela o pagamento cobre.');
    setBusy(true);
    setErro(null);
    try {
      await api.post(`/crm/mp-payments/${p.id}/importar`, { saleId: vendaId, parcelaId, valorBase: v });
      onDone();
    } catch (e) {
      setErro(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="links-list__item">
      <div className="links-list__main">
        <span className="tag-pill tag-pill--green">Pago no Mercado Pago</span>
        <strong>{formatBRL(p.valor)}</strong>
        <span className="muted small">
          {p.descricao || 'Sem descrição'}
          {p.pagador && <> · {p.pagador}</>}
        </span>
        <span className="muted small">
          {p.data && <>Pago em {formatDateTime(p.data)} · </>}
          {p.parcelas}x{p.liquido && <> · agência recebe {formatBRL(p.liquido)}</>} · pagamento {p.id}
        </span>
      </div>
      {!aberto ? (
        <Button onClick={() => void abrir()}>Dar baixa numa venda</Button>
      ) : (
        <div className="links-list__pick">
          {erro && <Alert>{erro}</Alert>}
          <label className="field">
            <span>Venda do Conta Azul</span>
            <select value={vendaId} onChange={(e) => void escolherVenda(e.target.value)}>
              <option value="">{vendas ? 'Escolha a venda…' : 'Carregando…'}</option>
              {vendas?.map((v) => (
                <option key={v.id} value={v.id}>
                  #{v.numero ?? '—'} · {v.clienteNome} · {formatBRL(v.valor)}
                </option>
              ))}
            </select>
          </label>
          {parcelas && (
            <label className="field">
              <span>Parcela</span>
              <select
                value={parcelaId}
                onChange={(e) => {
                  const x = parcelas.find((y) => y.id === e.target.value);
                  if (x) escolherParcela(x);
                  else setParcelaId('');
                }}
              >
                <option value="">Escolha a parcela…</option>
                {parcelas.map((x) => (
                  <option key={x.id} value={x.id} disabled={!x.aberta}>
                    {x.indice} · {nomeMetodo(x.metodo)} · {x.aberta ? `em aberto ${formatBRL(x.naoPago.toFixed(2))}` : 'paga'}
                  </option>
                ))}
              </select>
            </label>
          )}
          {parcelaId && (
            <label className="field">
              <span>Quanto da parcela este pagamento cobre</span>
              <input inputMode="decimal" value={valor} onChange={(e) => setValor(e.target.value)} />
            </label>
          )}
          <div className="row tight">
            <Button loading={busy} onClick={() => void confirmar()}>
              Dar baixa no Conta Azul
            </Button>
            <Button variant="ghost" onClick={() => setAberto(false)}>
              Cancelar
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
